/**
 * Provider Managed Services API (New Backend)
 *
 * REST endpoints for per-provider service authorization and provider service proposals.
 * Backend: accounts/views.py
 *
 * Endpoints:
 *   GET    /api/providers/<uuid>/services/            → Current authorized services
 *   PUT    /api/providers/<uuid>/services/            → Replace authorized services (full set)
 *   POST   /api/providers/service-proposals/          → Provider proposes a new service
 *
 * Operations approve/reject endpoints exist but belong to the internal-dashboard.
 */

import { AxiosError, isAxiosError } from 'axios'
import apiClient from '@/api/client'
import { ApiError, handleApiError } from '@/api/errorHandler'
import type {
  ProviderServicesResponse,
  SetProviderServicesInput,
  SubmitServiceProposalInput,
  SubmitServiceProposalResponse,
} from '@/types/providerServices'

// ============================================================================
// Authorized services
// ============================================================================

/**
 * Fetch the services a provider is currently authorized to offer.
 * Requires staff/ops or a company manager (ProfileCompany.is_manager) of the
 * company that owns the provider's entity.
 */
export async function getProviderServices(
  providerUuid: string
): Promise<ProviderServicesResponse> {
  const resp = await apiClient.get<ProviderServicesResponse>(
    `/api/providers/${providerUuid}/services/`
  )
  return resp.data
}

/**
 * Replace a provider's authorized services with the given set.
 *
 * This is a full replacement, not a delta — always send the complete selection.
 * An empty array is valid and clears every service.
 */
export async function setProviderServices(
  providerUuid: string,
  serviceTemplateIds: string[]
): Promise<ProviderServicesResponse> {
  const payload: SetProviderServicesInput = {
    service_template_ids: serviceTemplateIds,
  }
  const resp = await apiClient.put<ProviderServicesResponse>(
    `/api/providers/${providerUuid}/services/`,
    payload
  )
  return resp.data
}

// ============================================================================
// Proposals
// ============================================================================

/**
 * Submit a new service proposal. The caller must have a provider profile —
 * managers cannot propose on a provider's behalf (403 NOT_A_PROVIDER).
 */
export async function submitServiceProposal(
  input: SubmitServiceProposalInput
): Promise<SubmitServiceProposalResponse> {
  const resp = await apiClient.post<SubmitServiceProposalResponse>(
    '/api/providers/service-proposals/',
    input
  )
  return resp.data
}

// ============================================================================
// Error normalization
// ============================================================================

/**
 * These endpoints return two different 400 bodies:
 *
 *   1. The service-layer envelope, which handleApiError already understands:
 *        { success: false, error: "...", error_code: "VALIDATION_ERROR", timestamp: "..." }
 *
 *   2. DRF's own serializer failure, a bare top-level field dict, which
 *      handleApiError does NOT understand (it only looks under `details`):
 *        { "service_template_ids": ["This field is required."] }
 *
 * This normalizes both into a single ApiError with usable `message` and `details`,
 * so callers can rely on `getFieldErrors()` regardless of which shape arrived.
 */
export function toProviderServiceError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  if (!isAxiosError(err)) {
    const message = err instanceof Error ? err.message : String(err)
    return new ApiError(message, 'UNKNOWN_ERROR', 0)
  }

  const apiError = handleApiError(err as AxiosError)
  if (apiError.details) return apiError

  const data = err.response?.data
  const fieldErrors = extractFieldErrors(data)
  if (!fieldErrors) return apiError

  const message = Object.entries(fieldErrors)
    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
    .join('\n')

  return new ApiError(
    message || apiError.message,
    'VALIDATION_ERROR',
    apiError.status,
    fieldErrors
  )
}

/**
 * Recognize a bare DRF field-error dict: every value is a string or an array of strings,
 * and the payload carries none of the envelope's own keys.
 */
function extractFieldErrors(data: unknown): Record<string, string[]> | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null

  const record = data as Record<string, unknown>
  if ('error' in record || 'error_code' in record || 'success' in record) return null

  const result: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string') {
      result[key] = [value]
    } else if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      result[key] = value as string[]
    } else {
      return null
    }
  }

  return Object.keys(result).length > 0 ? result : null
}
